import { useSyncExternalStore } from 'react';
import { safeGet as readStore, safeSet as writeStore, safeRemove as removeStore } from './safeStorage';

/**
 * authStore — the single owner of auth tokens in Wyso.
 *
 * - The refresh token and username are persisted to `localStorage`.
 * - The access token is held IN MEMORY ONLY — never written to storage.
 * - All localStorage I/O is wrapped in try/catch so Safari private mode / quota
 *   errors never crash the app.
 *
 * Status lifecycle:
 *   'unknown'       – a refresh token exists in storage but no access token in memory
 *                     (normal post-reload state; caller must do a boot refresh)
 *   'anonymous'     – no refresh token; user must sign in
 *   'authenticated' – a live access token is held in memory
 *
 * @typedef {'unknown' | 'anonymous' | 'authenticated'} AuthStatus
 * @typedef {{ status: AuthStatus, username: string | null, accessToken: string | undefined }} AuthState
 */

const KEY_REFRESH = 'wyso.refreshToken';
const KEY_USERNAME = 'wyso.username';

// Safe localStorage helpers live in `lib/safeStorage.js` and are imported above under their
// original names — same try/catch semantics, now shared with the other module stores.

// ---------------------------------------------------------------------------
// Module-level state (initialised once on import)
// ---------------------------------------------------------------------------

/** @type {string | undefined} */
let _accessToken = undefined;

/** @returns {AuthState} */
function buildInitialState() {
  const refreshToken = readStore(KEY_REFRESH);
  if (refreshToken) {
    return { status: 'unknown', username: readStore(KEY_USERNAME), accessToken: undefined };
  }
  return { status: 'anonymous', username: null, accessToken: undefined };
}

/** @type {AuthState} */
let state = buildInitialState();

const listeners = new Set();

function emit() {
  for (const l of listeners) l();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** @returns {string | undefined} */
export function getAccessToken() {
  return _accessToken;
}

/** @returns {string | null} */
export function getRefreshToken() {
  return readStore(KEY_REFRESH);
}

/** @returns {string | null} */
export function getUsername() {
  return readStore(KEY_USERNAME);
}

/**
 * Called after a successful login.
 * Persists refreshToken + username; holds accessToken in memory only.
 * @param {{ accessToken: string, refreshToken: string, username: string }} params
 */
export function setSession({ accessToken, refreshToken, username } = {}) {
  if (refreshToken) writeStore(KEY_REFRESH, refreshToken);
  else removeStore(KEY_REFRESH);

  if (username) writeStore(KEY_USERNAME, username);
  else removeStore(KEY_USERNAME);

  _accessToken = accessToken || undefined;

  state = { status: refreshToken ? 'authenticated' : 'anonymous', username: username || null, accessToken: _accessToken };
  emit();
}

/**
 * Called after a successful token refresh (rotation).
 * Overwrites both the persisted refresh token and the in-memory access token.
 * Username is unchanged.
 * @param {{ accessToken: string, refreshToken: string }} params
 */
export function updateTokens({ accessToken, refreshToken } = {}) {
  if (refreshToken) writeStore(KEY_REFRESH, refreshToken);
  else removeStore(KEY_REFRESH);

  _accessToken = accessToken || undefined;

  state = { status: refreshToken ? 'authenticated' : 'anonymous', username: state.username, accessToken: _accessToken };
  emit();
}

/**
 * Clears all session data. Idempotent.
 */
export function clearSession() {
  removeStore(KEY_REFRESH);
  removeStore(KEY_USERNAME);
  _accessToken = undefined;
  state = { status: 'anonymous', username: null, accessToken: undefined };
  emit();
}

/**
 * Returns true when a refresh token is present in storage.
 * This is the routing gate — does NOT depend on the in-memory access token.
 * @returns {boolean}
 */
export function isAuthenticated() {
  return Boolean(readStore(KEY_REFRESH));
}

/**
 * The current auth snapshot (stable reference until a setter replaces it).
 * @returns {AuthState}
 */
export function getAuthSnapshot() {
  return state;
}

/**
 * React hook — subscribes to auth state changes via useSyncExternalStore.
 * @returns {AuthState}
 */
export function useAuth() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getAuthSnapshot,
    getAuthSnapshot,
  );
}

// ---------------------------------------------------------------------------
// Test-only reset (keeps module clean between test cases)
// ---------------------------------------------------------------------------

/** @internal Only exported for tests. Do not use in application code. */
export function _resetForTest() {
  _accessToken = undefined;
  state = buildInitialState();
  listeners.clear();
}
